import { DOCUMENT, Inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, map } from "rxjs";
import { GlobalConstants } from "../constants/constants";
import { ServiceUtilities } from "../utilities/service.utilities";

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private currentThemeSubject = new BehaviorSubject<string>('system');
  public currentTheme$ = this.currentThemeSubject.asObservable();
  public isDarkThemeActive$ = this.currentTheme$.pipe(
    map(theme => this.computeIsDarkActive(theme))
  );

  // Primary color is tuned independently per mode (see DEV-2560): each mode keeps its own stored
  // override, and applyColors() resolves whichever one is active into --bs-primary.
  private primaryColorLight: string = GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT();
  private primaryColorDark: string = GlobalConstants.DEFAULT_PRIMARY_COLOR_DARK();

  private primaryColorSubject = new BehaviorSubject<string>(GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT());
  public primaryColor$ = this.primaryColorSubject.asObservable();

  // Secondary mirrors primary's per-mode defaults: .btn-ms-secondary is the outline-style button
  // used in place of btn-outline-primary (see frontend rules), so it needs to match whichever
  // primary accent is active in order to still read as "the brand color, outlined."
  private secondaryColorLight: string = GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT();
  private secondaryColorDark: string = GlobalConstants.DEFAULT_PRIMARY_COLOR_DARK();

  private secondaryColorSubject = new BehaviorSubject<string>(GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT());
  public secondaryColor$ = this.secondaryColorSubject.asObservable();

  private navigationBgColorSubject = new BehaviorSubject<string | null>(null);
  public navigationBgColor$ = this.navigationBgColorSubject.asObservable();

  private mediaQueryList: MediaQueryList | null = null;
  private mediaQueryHandler = () => {
    if (this.currentThemeSubject.value === 'system') {
      this.updateDocumentTheme('system');
      this.currentThemeSubject.next('system');
      this.applyColors();
    }
  };

  constructor(@Inject(DOCUMENT) private document: Document) {
    const savedTheme = this.getStoredTheme() || 'system';
    this.setTheme(savedTheme);
    
    this.initializeColors();
    
    this.listenForSystemThemeChanges();
  }

  private getStoredTheme(): string | null {
    if (!ServiceUtilities.isWindowAndLocalStorageUndefined()) {
      try {
        return localStorage.getItem('theme');
      } catch (error) {
        console.warn('Failed to access localStorage:', error);
        return null;
      }
    }
    return null;
  }

  private setStoredTheme(theme: string): void {
    if (!ServiceUtilities.isWindowAndLocalStorageUndefined()) {
      try {
        localStorage.setItem('theme', theme);
      } catch (error) {
        console.warn('Failed to save theme to localStorage:', error);
      }
    }
  }

  public setTheme(theme: string): void {
    this.currentThemeSubject.next(theme);
    this.setStoredTheme(theme);
    this.updateDocumentTheme(theme);
    this.applyColors();
  }

  private computeIsDarkActive(theme: string): boolean {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      } catch (error) {
        console.warn('Failed to access matchMedia:', error);
      }
    }
    
    return false;
  }

  private updateDocumentTheme(theme: string): void {
    if (theme === 'dark') {
      this.document.documentElement.setAttribute('data-bs-theme', 'dark');
    } else if (theme === 'light') {
      this.document.documentElement.setAttribute('data-bs-theme', 'light');
    } else {
      const isDark = this.computeIsDarkActive(theme);
      this.document.documentElement.setAttribute('data-bs-theme', isDark ? 'dark' : 'light');
    }
  }

  private listenForSystemThemeChanges(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
        this.mediaQueryList.addEventListener('change', this.mediaQueryHandler);
      } catch (error) {
        console.warn('Failed to set up system theme change listener:', error);
      }
    }
  }

  public ngOnDestroy(): void {
    if (this.mediaQueryList) {
      this.mediaQueryList.removeEventListener('change', this.mediaQueryHandler);
    }
  }

  public getCurrentTheme(): string {
    return this.currentThemeSubject.value;
  }
  
  public isDarkThemeActive(): boolean {
    return this.computeIsDarkActive(this.currentThemeSubject.value);
  }
  
  //region Theme Management
  private initializeColors(): void {
    const savedPrimaryLight = this.getStoredColor('primaryColorLight');
    const savedPrimaryDark = this.getStoredColor('primaryColorDark');
    const savedSecondaryLight = this.getStoredColor('secondaryColorLight');
    const savedSecondaryDark = this.getStoredColor('secondaryColorDark');
    const savedNavigationBg = this.getStoredColor('navigationBgColor');

    if (savedPrimaryLight) {
      this.primaryColorLight = savedPrimaryLight;
    }

    if (savedPrimaryDark) {
      this.primaryColorDark = savedPrimaryDark;
    }

    if (savedSecondaryLight) {
      this.secondaryColorLight = savedSecondaryLight;
    }

    if (savedSecondaryDark) {
      this.secondaryColorDark = savedSecondaryDark;
    }

    if (savedNavigationBg) {
      this.setNavigationBgColor(savedNavigationBg);
    }

    this.applyColors();
  }

  private getStoredColor(key: string): string | null {
    if (!ServiceUtilities.isWindowAndLocalStorageUndefined()) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.warn(`Failed to access localStorage for ${key}:`, error);
        return null;
      }
    }
    return null;
  }

  private setStoredColor(key: string, value: string): void {
    if (!ServiceUtilities.isWindowAndLocalStorageUndefined()) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn(`Failed to save ${key} to localStorage:`, error);
      }
    }
  }

  private removeStoredColor(key: string): void {
    if (!ServiceUtilities.isWindowAndLocalStorageUndefined()) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key} from localStorage:`, error);
      }
    }
  }

  public setPrimaryColor(hexColor: string): void {
    if (this.isDarkThemeActive()) {
      this.primaryColorDark = hexColor;
      this.setStoredColor('primaryColorDark', hexColor);
    } else {
      this.primaryColorLight = hexColor;
      this.setStoredColor('primaryColorLight', hexColor);
    }
    this.applyColors();
  }

  public resetPrimaryColor(): void {
    this.primaryColorLight = GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT();
    this.primaryColorDark = GlobalConstants.DEFAULT_PRIMARY_COLOR_DARK();
    this.setStoredColor('primaryColorLight', this.primaryColorLight);
    this.setStoredColor('primaryColorDark', this.primaryColorDark);
    this.applyColors();
  }

  public setSecondaryColor(hexColor: string): void {
    if (this.isDarkThemeActive()) {
      this.secondaryColorDark = hexColor;
      this.setStoredColor('secondaryColorDark', hexColor);
    } else {
      this.secondaryColorLight = hexColor;
      this.setStoredColor('secondaryColorLight', hexColor);
    }
    this.applyColors();
  }

  public resetSecondaryColor(): void {
    this.secondaryColorLight = GlobalConstants.DEFAULT_PRIMARY_COLOR_LIGHT();
    this.secondaryColorDark = GlobalConstants.DEFAULT_PRIMARY_COLOR_DARK();
    this.setStoredColor('secondaryColorLight', this.secondaryColorLight);
    this.setStoredColor('secondaryColorDark', this.secondaryColorDark);
    this.applyColors();
  }

  public setNavigationBgColor(hexColor: string | null): void {
    this.navigationBgColorSubject.next(hexColor);
    if (hexColor) {
      this.setStoredColor('navigationBgColor', hexColor);
    } else {
      this.removeStoredColor('navigationBgColor');
    }
    this.applyColors();
  }

  public getPrimaryColor(): string {
    return this.primaryColorSubject.value;
  }

  public getSecondaryColor(): string {
    return this.secondaryColorSubject.value;
  }

  public getNavigationBgColor(): string | null {
    return this.navigationBgColorSubject.value;
  }

  private applyColors(): void {
    const root = this.document.documentElement;
    const isDark = this.isDarkThemeActive();
    const primary = isDark ? this.primaryColorDark : this.primaryColorLight;
    const secondary = isDark ? this.secondaryColorDark : this.secondaryColorLight;
    const navigationBg = this.navigationBgColorSubject.value;

    // Convert hex to RGB for CSS custom properties
    const primaryRgb = this.hexToRgb(primary);
    const secondaryRgb = this.hexToRgb(secondary);

    // Update CSS custom properties
    root.style.setProperty('--bs-primary', primary);
    root.style.setProperty('--ms-secondary', secondary);

    if (navigationBg) {
      const navigationBgRgb = this.hexToRgb(navigationBg);
      root.style.setProperty('--ms-navigation-bg', navigationBg);
      if (navigationBgRgb) {
        root.style.setProperty('--ms-navigation-bg-rgb', `${navigationBgRgb.r}, ${navigationBgRgb.g}, ${navigationBgRgb.b}`);
      }
    } else {
      root.style.removeProperty('--ms-navigation-bg');
      root.style.removeProperty('--ms-navigation-bg-rgb');
    }

    if (primaryRgb) {
      root.style.setProperty('--bs-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }

    if (secondaryRgb) {
      root.style.setProperty('--ms-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    }

    this.primaryColorSubject.next(primary);
    this.secondaryColorSubject.next(secondary);
  }

  private hexToRgb(hex: string): {r: number, g: number, b: number} | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }
  //endregion Theme Management
}
